// TablePagination.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
    Typography, Box, IconButton, Select, MenuItem, FormControl
} from '@mui/material';
import { FirstPage, LastPage, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { toPersianDigits } from '../../../../utils/convertNumbers';

export default function TablePagination({
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    pageSizeOptions = [10, 15, 25, 50],
    showPageInfo = true,
    showPageNumber = true,
    responsive = false,
}) {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    const startItem = totalCount === 0 ? 0 : page * rowsPerPage + 1;
    const endItem = Math.min((page + 1) * rowsPerPage, totalCount);

    // Handle page change - accepts either page number directly or event
    const handlePageChange = (newPage) => {
        if (typeof onPageChange === 'function') {
            onPageChange(newPage);
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            ...(responsive && {
                flexWrap: 'wrap',
                gap: 2
            })
        }}>
            {/* Rows per page selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ direction: 'rtl' }}>
                    تعداد ردیف در هر صفحه:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 60 }}>
                    <Select
                        value={rowsPerPage}
                        onChange={onRowsPerPageChange}
                        sx={{ 
                            '& .MuiSelect-select': {
                                fontFamily: 'inherit',
                                textAlign: 'center'
                            }
                        }}
                    >
                        {pageSizeOptions.map((size) => (
                            <MenuItem key={size} value={size}>
                                {toPersianDigits(size)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Page info */}
            {showPageInfo && (
                <Typography variant="body2" sx={{ direction: 'rtl' }}>
                تعداد {toPersianDigits(startItem)}–{toPersianDigits(endItem)} از {toPersianDigits(totalCount)}
                </Typography>
            )}

            {/* Navigation buttons (RTL: backward on the right, forward on the left) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Right side: First (>|) and Previous (>) — toward the start */}
                <IconButton
                    onClick={() => handlePageChange(0)}
                    disabled={page === 0}
                    aria-label="صفحه اول"
                    size="small"
                >
                    <LastPage />
                </IconButton>
                <IconButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 0}
                    aria-label="صفحه قبلی"
                    size="small"
                >
                    <KeyboardArrowRight />
                </IconButton>

                {/* Center: Page number display */}
                {showPageNumber && (
                    <Typography variant="body2" sx={{ mx: 2, minWidth: 60, textAlign: 'center' }}>
                        صفحه {toPersianDigits(page + 1)} از {toPersianDigits(totalPages || 1)}
                    </Typography>
                )}

                {/* Left side: Next (<) and Last (|<) — toward the end */}
                <IconButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    aria-label="صفحه بعدی"
                    size="small"
                >
                    <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                    onClick={() => handlePageChange(totalPages - 1)}
                    disabled={page >= totalPages - 1}
                    aria-label="صفحه آخر"
                    size="small"
                >
                    <FirstPage />
                </IconButton>
            </Box>
        </Box>
    );
}

TablePagination.propTypes = {
    totalCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    showPageInfo: PropTypes.bool,
    showPageNumber: PropTypes.bool,
    responsive: PropTypes.bool,
};

